/**
 * Auditoria unificada (admin/coord/audin/presidência): uma linha do tempo de TUDO
 * que muda no sistema — quem, o quê, quando e o antes→depois (checklist e plano).
 * Funde o log genérico com os eventos de evidência do plano. Lê `/admin/auditoria`
 * com filtros por área/período/usuário + busca; clicar numa linha abre o histórico
 * daquele item. Substitui a antiga "Auditoria de Evidências".
 */
import { useCallback, useEffect, useState } from "react";
import type { EventoAuditoria, FiltroAuditoria } from "../../lib/api.ts";
import { ApiError, apiClient } from "../../lib/api.ts";
import { mensagemAmigavel } from "../../lib/erros.ts";
import { type Coluna, PageHeader, Tabela } from "../ui/kit.tsx";

function quando(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Áreas para o filtro + a cor do "chip" de ação. `valor` é o prefixo da ação. */
const AREAS: readonly { valor: string; rotulo: string; cor: string }[] = [
  { valor: "", rotulo: "Todas as áreas", cor: "var(--slate-500)" },
  { valor: "auth.", rotulo: "Login/Acesso", cor: "#C0392B" },
  { valor: "checklist.", rotulo: "Checklist", cor: "var(--teal-600)" },
  { valor: "escopo.", rotulo: "Escopo", cor: "#0E7C86" },
  { valor: "plano.item", rotulo: "Plano de Ação", cor: "#585FA0" },
  { valor: "plano.evidencia", rotulo: "Evid. do Plano", cor: "#B5651D" },
  { valor: "evidencia.", rotulo: "Evid. do Checklist", cor: "#A0722D" },
  { valor: "relatorio.", rotulo: "Relatórios", cor: "#1C7C54" },
  { valor: "supervisao.", rotulo: "Supervisão", cor: "#7B4FB0" },
  { valor: "imgr.", rotulo: "Form. público", cor: "#2E6DA4" },
  { valor: "ciclo.", rotulo: "Ciclos", cor: "#2D74B8" },
  { valor: "usuario.", rotulo: "Usuários", cor: "var(--slate-600)" },
  { valor: "matriz.", rotulo: "Matriz", cor: "#8A5A00" },
  { valor: "migracao.", rotulo: "Migração", cor: "#B5651D" },
];

function corDaAcao(acao: string): string {
  return AREAS.find((a) => a.valor && acao.startsWith(a.valor))?.cor ?? "var(--slate-500)";
}

const LIMITE = 50;
const filtroVazio: FiltroAuditoria = {};

export function Auditoria({ token }: { token: string | null }) {
  const [filtro, setFiltro] = useState<FiltroAuditoria>(filtroVazio);
  const [eventos, setEventos] = useState<EventoAuditoria[]>([]);
  const [temMais, setTemMais] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(
    async (offset: number) => {
      if (!token) {
        return;
      }
      setCarregando(true);
      setErro(null);
      try {
        const r = await apiClient.auditoria({ ...filtro, offset, limite: LIMITE }, token);
        setEventos((prev) => (offset === 0 ? r.eventos : [...prev, ...r.eventos]));
        setTemMais(r.temMais);
      } catch (e) {
        setErro(e instanceof ApiError ? mensagemAmigavel(e) : "Falha ao carregar a auditoria.");
      } finally {
        setCarregando(false);
      }
    },
    [token, filtro],
  );

  // Recarrega do zero sempre que os filtros mudam.
  useEffect(() => {
    void carregar(0);
  }, [carregar]);

  const patch = (p: Partial<FiltroAuditoria>) => setFiltro((f) => ({ ...f, ...p }));
  const focarItem = (e: EventoAuditoria) =>
    setFiltro((f) => ({ ...f, entidade: e.entidade, entidadeId: e.entidadeId ?? undefined }));

  const colunas: Coluna<EventoAuditoria>[] = [
    { rotulo: "Quando", largura: "132px", celula: (e) => <span className="spec">{quando(e.em)}</span> },
    {
      rotulo: "Quem",
      largura: "180px",
      celula: (e) => (
        <span>
          {e.autorNome ?? e.autorId ?? "—"}
          {e.autorPapel && <span className="spec"> · {e.autorPapel}</span>}
        </span>
      ),
    },
    {
      rotulo: "Ação",
      largura: "170px",
      celula: (e) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span
            aria-hidden="true"
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: corDaAcao(e.acao),
              flex: "0 0 auto",
            }}
          />
          {e.acao}
        </span>
      ),
    },
    {
      rotulo: "Onde",
      largura: "150px",
      celula: (e) => (
        <span className="spec">
          {e.entidade}
          {e.entidadeId ? ` · ${e.entidadeId}` : ""}
        </span>
      ),
    },
    { rotulo: "O que mudou", celula: (e) => e.resumo ?? <span className="spec">—</span> },
  ];

  return (
    <div className="dk-page">
      <PageHeader titulo="Auditoria" sub="Tudo que muda no sistema — quem, o quê, quando e o antes→depois" />

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end", margin: "0 0 14px" }}>
        <label style={{ display: "grid", gap: 4, fontSize: 12, color: "var(--text-muted)" }}>
          Área
          <select
            className="m-input"
            value={filtro.acao ?? ""}
            onChange={(ev) => patch({ acao: ev.target.value || undefined })}
          >
            {AREAS.map((a) => (
              <option key={a.rotulo} value={a.valor}>
                {a.rotulo}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: "grid", gap: 4, fontSize: 12, color: "var(--text-muted)" }}>
          De
          <input
            type="date"
            className="m-input"
            value={filtro.de ?? ""}
            onChange={(ev) => patch({ de: ev.target.value || undefined })}
          />
        </label>
        <label style={{ display: "grid", gap: 4, fontSize: 12, color: "var(--text-muted)" }}>
          Até
          <input
            type="date"
            className="m-input"
            value={filtro.ate ?? ""}
            onChange={(ev) => patch({ ate: ev.target.value || undefined })}
          />
        </label>
        <label
          style={{
            display: "grid",
            gap: 4,
            fontSize: 12,
            color: "var(--text-muted)",
            flex: 1,
            minWidth: 180,
          }}
        >
          Buscar
          <input
            type="search"
            className="m-input"
            placeholder="Item, motivo, nome…"
            value={filtro.q ?? ""}
            onChange={(ev) => patch({ q: ev.target.value || undefined })}
          />
        </label>
        {(filtro.entidadeId || filtro.acao || filtro.q || filtro.de || filtro.ate) && (
          <button type="button" className="m-btn" onClick={() => setFiltro(filtroVazio)}>
            Limpar
          </button>
        )}
      </div>

      {filtro.entidadeId && (
        <p className="spec" style={{ margin: "0 0 10px" }}>
          Histórico de <b>{filtro.entidadeId}</b> ({filtro.entidade}) ·{" "}
          <button
            type="button"
            style={{ border: 0, background: "none", cursor: "pointer", color: "var(--teal-700)" }}
            onClick={() => patch({ entidade: undefined, entidadeId: undefined })}
          >
            ver tudo
          </button>
        </p>
      )}

      {erro && <p style={{ color: "var(--danger)", marginBottom: 12 }}>{erro}</p>}

      <Tabela
        colunas={colunas}
        linhas={eventos}
        chave={(e) => e.id}
        aoClicar={focarItem}
        vazio={carregando ? "Carregando…" : "Nenhum evento para este filtro."}
      />

      {temMais && (
        <div style={{ marginTop: 14, textAlign: "center" }}>
          <button
            type="button"
            className="m-btn"
            disabled={carregando}
            onClick={() => void carregar(eventos.length)}
          >
            {carregando ? "Carregando…" : "Carregar mais"}
          </button>
        </div>
      )}
    </div>
  );
}
